import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";
import {
  Package,
  Search,
  Upload,
  ArrowLeft,
  Download,
  AlertCircle,
  Edit,
} from "lucide-react";
import Button from "shared/Button";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { toast } from "react-toastify";
import {
  getInventoryDashboard,
  getInventoryStatistics,
  importInventory,
  exportInventory,
  getInventory,
  getSkuImports,
  bulkUpdateInventoryStatus,
  updateInventoryStatus,
  DashboardInventoryItem,
  InventoryStatistics,
  InventoryItem,
} from "core/Services/InventoryServices";
import { getUsers } from "core/Services/AdminServices";
import { useRef } from "react";

const InventoryDashboard = () => {
  const { t } = useTranslation();
  const { merchantId } = useParams();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [isExporting, setIsExporting] = useState<boolean>(false);
  const [merchantInfo, setMerchantInfo] = useState<any>(null);
  const [statistics, setStatistics] = useState<InventoryStatistics | null>(
    null
  );
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  // Bulk edit states
  const [showBulkEditModal, setShowBulkEditModal] = useState(false);
  const [selectedSku, setSelectedSku] = useState<string>("");
  const [pendingImports, setPendingImports] = useState<InventoryItem[]>([]);
  const [bulkEditItems, setBulkEditItems] = useState<
    Array<{
      id: number;
      quantity_status: "received" | "missing_quantity";
      missing_quantity_number?: number;
    }>
  >([]);
  const [isSavingBulk, setIsSavingBulk] = useState(false);

  // Inventory items (import records)
  const [inventoryItems, setInventoryItems] = useState<InventoryItem[]>([]);
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [editingStatusId, setEditingStatusId] = useState<number | null>(null);
  const [tempStatus, setTempStatus] = useState<string>("");
  const [tempMissingQty, setTempMissingQty] = useState<number>(0);
  const [isSavingStatus, setIsSavingStatus] = useState(false);
  const [selectedItems, setSelectedItems] = useState<number[]>([]);
  const [isBulkUpdating, setIsBulkUpdating] = useState(false);
  const [refetchTrigger, setRefetchTrigger] = useState(0);

  useEffect(() => {
    const fetchMerchantInfo = async () => {
      if (!merchantId) return;

      try {
        const response = await getUsers({ getAll: 1 });
        const users = Array.isArray(response.data) ? response.data : [];
        const merchant = users.find((u: any) => u.id === parseInt(merchantId));
        if (merchant) {
          setMerchantInfo({
            id: merchant.id,
            name: `${merchant.first_name} ${merchant.last_name}`,
            businessName: merchant.business_name || merchant.email,
          });
        }
      } catch (error) {
        console.error("Error fetching merchant info:", error);
        toast.error(t("inventory.errorFetchingMerchant"));
      }
    };

    fetchMerchantInfo();
  }, [merchantId, t]);

  useEffect(() => {
    const fetchDashboard = async () => {
      if (!merchantId) return;

      setIsLoading(true);
      try {
        const response = await getInventory(
          parseInt(merchantId),
          {
            search: searchTerm,
            status: statusFilter || undefined,
            per_page: 50,
          },
          currentPage
        );
        console.log("Pagination response:", response);

        // Handle both direct pagination and resource collection response
        const items = response.data || [];
        const lastPage = response.last_page || response.meta?.last_page || 1;
        const totalCount = response.total || response.meta?.total || 0;

        setInventoryItems(Array.isArray(items) ? items : []);
        setTotalPages(lastPage);
        setTotal(totalCount);
      } catch (error) {
        console.error("Error fetching inventory:", error);
        toast.error(t("inventory.errorFetchingInventory"));
        setInventoryItems([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDashboard();
  }, [merchantId, searchTerm, statusFilter, currentPage, refetchTrigger, t]);

  useEffect(() => {
    const fetchStatistics = async () => {
      if (!merchantId) return;

      try {
        const stats = await getInventoryStatistics(parseInt(merchantId));
        setStatistics(stats);
      } catch (error) {
        console.error("Error fetching statistics:", error);
      }
    };

    fetchStatistics();
  }, [merchantId]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && merchantId) {
      const validTypes = [
        "application/vnd.ms-excel",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "application/vnd.oasis.opendocument.spreadsheet",
      ];

      if (
        !validTypes.includes(file.type) &&
        !file.name.match(/\.(xlsx|xls|ods)$/)
      ) {
        toast.error(
          t("inventory.invalidFileType") ||
            "Please select a valid Excel file (.xlsx, .xls)"
        );
        return;
      }

      setIsUploading(true);
      try {
        const result = await importInventory(parseInt(merchantId), file);
        toast.success(
          result.message ||
            `Import successful! ${result.imported_count} items imported. Please review and approve each import.`
        );

        // Refresh the dashboard and statistics
        setRefetchTrigger((prev) => prev + 1);
      } catch (error: any) {
        console.error("Error importing file:", error);
        toast.error(
          error.response?.data?.message ||
            t("inventory.importError") ||
            "Error importing file"
        );
      } finally {
        setIsUploading(false);
      }

      e.target.value = "";
    }
  };

  const handleExport = async () => {
    if (!merchantId) return;

    setIsExporting(true);
    try {
      await exportInventory(parseInt(merchantId));
      toast.success(
        t("inventory.exportSuccess") || "Inventory exported successfully!"
      );
    } catch (error) {
      console.error("Error exporting inventory:", error);
      toast.error(t("inventory.exportError") || "Error exporting inventory");
    } finally {
      setIsExporting(false);
    }
  };

  const handleViewSkuDetails = (sku: string) => {
    navigate(`/inventory/${merchantId}/sku/${encodeURIComponent(sku)}`);
  };

  const handleBulkEdit = async (sku: string) => {
    if (!merchantId) return;

    try {
      // Fetch all imports for this SKU
      const response = await getSkuImports(parseInt(merchantId), sku);
      const imports: InventoryItem[] = Array.isArray(response.data)
        ? response.data
        : [];

      // Filter only under_review items
      const underReviewImports = imports.filter(
        (item) => item.quantity_status === "under_review"
      );

      if (underReviewImports.length === 0) {
        toast.info("No pending items to edit for this SKU");
        return;
      }

      // Initialize bulk edit items with default 'received' status
      const initialBulkItems = underReviewImports.map((item) => ({
        id: item.id,
        quantity_status: "received" as "received" | "missing_quantity",
        missing_quantity_number: 0,
      }));

      setSelectedSku(sku);
      setPendingImports(underReviewImports);
      setBulkEditItems(initialBulkItems);
      setShowBulkEditModal(true);
    } catch (error) {
      console.error("Error fetching SKU imports:", error);
      toast.error("Failed to load pending imports");
    }
  };

  const handleBulkStatusChange = (
    id: number,
    status: "received" | "missing_quantity"
  ) => {
    setBulkEditItems((prev) =>
      prev.map((item) =>
        item.id === id
          ? {
              ...item,
              quantity_status: status,
              missing_quantity_number:
                status === "received" ? 0 : item.missing_quantity_number,
            }
          : item
      )
    );
  };

  const handleBulkMissingQuantityChange = (id: number, value: number) => {
    setBulkEditItems((prev) =>
      prev.map((item) =>
        item.id === id ? { ...item, missing_quantity_number: value } : item
      )
    );
  };

  const handleSaveBulkEdit = async () => {
    if (!merchantId) return;

    setIsSavingBulk(true);
    try {
      const response = await bulkUpdateInventoryStatus(
        parseInt(merchantId),
        bulkEditItems
      );

      toast.success(
        `Successfully updated ${response.updated_count} item(s)${
          response.failed_count > 0 ? `. Failed: ${response.failed_count}` : ""
        }`
      );

      if (response.failed_count > 0) {
        response.failed.forEach((fail) => {
          toast.error(`Item ${fail.id}: ${fail.error}`);
        });
      }

      setShowBulkEditModal(false);
      setRefetchTrigger((prev) => prev + 1);
    } catch (error: any) {
      console.error("Error bulk updating:", error);
      toast.error(error?.response?.data?.message || "Failed to update items");
    } finally {
      setIsSavingBulk(false);
    }
  };

  const handlePageChange = (page: number) => {
    console.log("Page change requested:", page);
    setCurrentPage(page);
  };

  const handleStatusEdit = (item: InventoryItem) => {
    setEditingStatusId(item.id);
    setTempStatus(item.quantity_status);
    // Set initial missing quantity to existing value or 1 (minimum valid value)
    const initialMissingQty = item.missing_quantity_number || 1;
    setTempMissingQty(
      initialMissingQty >= item.quantity ? 1 : initialMissingQty
    );
  };

  const handleStatusSave = async (itemId: number) => {
    if (!tempStatus) return;

    setIsSavingStatus(true);
    try {
      await updateInventoryStatus(itemId, {
        quantity_status: tempStatus as
          | "under_review"
          | "received"
          | "missing_quantity",
        missing_quantity_number:
          tempStatus === "missing_quantity" ? tempMissingQty : undefined,
      });

      toast.success(
        t("inventory.statusUpdateSuccess") || "Status updated successfully!"
      );
      setEditingStatusId(null);
      setRefetchTrigger((prev) => prev + 1);
    } catch (error: any) {
      console.error("Error updating status:", error);
      toast.error(
        error?.response?.data?.message ||
          t("inventory.statusUpdateError") ||
          "Error updating status"
      );
    } finally {
      setIsSavingStatus(false);
    }
  };

  const handleStatusCancel = () => {
    setEditingStatusId(null);
    setTempStatus("");
    setTempMissingQty(0);
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      // Only select items with "under_review" status
      const selectableItems = inventoryItems
        .filter(
          (item: any) =>
            item.quantity_status !== "received" &&
            item.quantity_status !== "missing_quantity"
        )
        .map((item: any) => item.id);
      setSelectedItems(selectableItems);
    } else {
      setSelectedItems([]);
    }
  };

  const handleSelectItem = (itemId: number, checked: boolean) => {
    if (checked) {
      setSelectedItems([...selectedItems, itemId]);
    } else {
      setSelectedItems(selectedItems.filter((id) => id !== itemId));
    }
  };

  const handleBulkMarkAsReceived = async () => {
    if (selectedItems.length === 0) {
      toast.error(
        t("inventory.selectItemsFirst") || "Please select items first"
      );
      return;
    }

    try {
      setIsBulkUpdating(true);
      const items = selectedItems.map((id) => ({
        id,
        quantity_status: "received" as const,
        missing_quantity_number: 0,
      }));

      await bulkUpdateInventoryStatus(Number(merchantId), items);
      toast.success(
        t("inventory.bulkUpdateSuccess") ||
          `${selectedItems.length} items marked as received`
      );
      setSelectedItems([]);
      setRefetchTrigger((prev) => prev + 1);
    } catch (error: any) {
      console.error("Bulk update error:", error);
      toast.error(
        error.response?.data?.message ||
          t("inventory.bulkUpdateError") ||
          "Failed to update items"
      );
    } finally {
      setIsBulkUpdating(false);
    }
  };

  return (
    <div className="flex gap-4 flex-col p-4 sm:p-6 max-w-7xl mx-auto">
      <div className="bg-white rounded-md shadow-sm p-4 sm:p-6 flex gap-4 flex-col">
        {/* Header with Back Button */}
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/inventory")}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              title={t("common.back")}
            >
              <ArrowLeft size={24} />
            </button>
            <Package size={32} className="text-primary" />
            <div>
              <h1 className="text-2xl font-bold text-gray-800">
                {t("inventory.dashboard")}
              </h1>
              {merchantInfo && (
                <p className="text-sm text-gray-600 mt-1">
                  {merchantInfo.businessName} - {merchantInfo.name}
                </p>
              )}
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              className="flex items-center gap-2"
              onClick={handleExport}
              icon={<Download size={20} />}
              disabled={isExporting}
              variant="outlined"
            >
              <span>
                {isExporting ? t("inventory.exporting") : t("inventory.export")}
              </span>
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls,.ods,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={handleFileChange}
              className="hidden"
            />
            <Button
              className="flex items-center gap-2"
              onClick={handleImportClick}
              icon={<Upload size={20} />}
              disabled={isUploading}
            >
              <span>
                {isUploading
                  ? t("inventory.importing")
                  : t("inventory.importFile")}
              </span>
            </Button>
          </div>
        </div>

        {/* Statistics Cards */}
        {statistics && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <p className="text-sm text-blue-600 font-medium">
                {t("inventory.totalItems") || "Total Records"}
              </p>
              <p className="text-2xl font-bold text-blue-900 mt-1">
                {statistics.total_items}
              </p>
            </div>
            <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
              <p className="text-sm text-yellow-600 font-medium">
                {t("inventory.underReview") || "Under Review"}
              </p>
              <p className="text-2xl font-bold text-yellow-900 mt-1">
                {statistics.under_review}
              </p>
            </div>
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <p className="text-sm text-green-600 font-medium">
                {t("inventory.received") || "Received"}
              </p>
              <p className="text-2xl font-bold text-green-900 mt-1">
                {statistics.received}
              </p>
            </div>
            <div className="bg-red-50 p-4 rounded-lg border border-red-200">
              <p className="text-sm text-red-600 font-medium">
                {t("inventory.missingQuantity") || "Missing Quantity"}
              </p>
              <p className="text-2xl font-bold text-red-900 mt-1">
                {statistics.missing_quantity}
              </p>
            </div>
          </div>
        )}

        {/* Search Box */}
        <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1 max-w-md">
              <Search
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                size={20}
              />
              <input
                type="text"
                placeholder={
                  t("inventory.searchBySku") ||
                  "Search by SKU, Product Name, or Manufacturer"
                }
                value={searchTerm}
                onChange={handleSearch}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <div className="w-full sm:w-48">
              <select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="">
                  {t("inventory.allStatus") || "All Status"}
                </option>
                <option value="under_review">
                  {t("inventory.underReview") || "Under Review"}
                </option>
                <option value="received">
                  {t("inventory.received") || "Received"}
                </option>
                <option value="missing_quantity">
                  {t("inventory.missingQuantity") || "Missing Quantity"}
                </option>
              </select>
            </div>
            <button
              onClick={handleBulkMarkAsReceived}
              disabled={selectedItems.length === 0 || isBulkUpdating}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center gap-2 whitespace-nowrap"
            >
              {isBulkUpdating ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  {t("inventory.updating") || "Updating..."}
                </>
              ) : (
                <>
                  {t("inventory.markSelectedReceived") ||
                    "Mark Selected as Received"}
                  {selectedItems.length > 0 && (
                    <span className="bg-green-700 px-2 py-0.5 rounded-full text-xs">
                      {selectedItems.length}
                    </span>
                  )}
                </>
              )}
            </button>
          </div>
        </div>

        {/* Dashboard Table */}
        <div className="overflow-x-auto rounded-lg border border-gray-200">
          <table className="w-full min-w-[900px]">
            <thead>
              <tr className="bg-gray-50">
                <th className="text-center p-3 font-medium text-xs tracking-wider text-gray-500 uppercase w-12">
                  <input
                    type="checkbox"
                    checked={
                      inventoryItems.length > 0 &&
                      inventoryItems.filter(
                        (item: any) =>
                          item.quantity_status !== "received" &&
                          item.quantity_status !== "missing_quantity"
                      ).length > 0 &&
                      selectedItems.length ===
                        inventoryItems.filter(
                          (item: any) =>
                            item.quantity_status !== "received" &&
                            item.quantity_status !== "missing_quantity"
                        ).length
                    }
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                  />
                </th>
                <th className="text-left p-3 font-medium text-xs tracking-wider text-gray-500 uppercase">
                  {t("inventory.sku")}
                </th>
                <th className="text-left p-3 font-medium text-xs tracking-wider text-gray-500 uppercase">
                  {t("inventory.productName")}
                </th>
                <th className="text-left p-3 font-medium text-xs tracking-wider text-gray-500 uppercase">
                  {t("inventory.manufacturer")}
                </th>
                <th className="text-left p-3 font-medium text-xs tracking-wider text-gray-500 uppercase">
                  {t("inventory.importDate") || "Import Date"}
                </th>
                <th className="text-left p-3 font-medium text-xs tracking-wider text-gray-500 uppercase">
                  {t("inventory.originalQuantity") || "Original Qty"}
                </th>
                <th className="text-left p-3 font-medium text-xs tracking-wider text-gray-500 uppercase">
                  {t("inventory.available") || "Available"}
                </th>
                <th className="text-left p-3 font-medium text-xs tracking-wider text-gray-500 uppercase">
                  {t("inventory.missing") || "Missing"}
                </th>
                <th className="text-left p-3 font-medium text-xs tracking-wider text-gray-500 uppercase">
                  {t("inventory.price")}
                </th>
                <th className="text-left p-3 font-medium text-xs tracking-wider text-gray-500 uppercase">
                  {t("inventory.status") || "Status"}
                </th>
                <th className="text-left p-3 font-medium text-xs tracking-wider text-gray-500 uppercase">
                  {t("inventory.editStatus") || "Edit Status"}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {isLoading ? (
                Array(5)
                  .fill(0)
                  .map((_, index) => (
                    <tr key={index}>
                      <td className="py-4 px-3 text-center">
                        <Skeleton width={16} height={16} />
                      </td>
                      <td className="py-4 px-3">
                        <Skeleton width={100} />
                      </td>
                      <td className="py-4 px-3">
                        <Skeleton width={150} />
                      </td>
                      <td className="py-4 px-3">
                        <Skeleton width={100} />
                      </td>
                      <td className="py-4 px-3">
                        <Skeleton width={120} />
                      </td>
                      <td className="py-4 px-3">
                        <Skeleton width={60} />
                      </td>
                      <td className="py-4 px-3">
                        <Skeleton width={60} />
                      </td>
                      <td className="py-4 px-3">
                        <Skeleton width={60} />
                      </td>
                      <td className="py-4 px-3">
                        <Skeleton width={60} />
                      </td>
                      <td className="py-4 px-3">
                        <Skeleton width={80} />
                      </td>
                      <td className="py-4 px-3">
                        <Skeleton width={80} />
                      </td>
                    </tr>
                  ))
              ) : inventoryItems.length > 0 ? (
                inventoryItems.map((item) => (
                  <tr
                    key={item.id}
                    className={`hover:bg-gray-50 transition-colors ${
                      item.quantity_status === "under_review"
                        ? "bg-yellow-50"
                        : ""
                    }`}
                  >
                    <td className="py-4 px-3 text-center">
                      <input
                        type="checkbox"
                        checked={selectedItems.includes(item.id)}
                        onChange={(e) =>
                          handleSelectItem(item.id, e.target.checked)
                        }
                        disabled={
                          item.quantity_status === "received" ||
                          item.quantity_status === "missing_quantity"
                        }
                        className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      />
                    </td>
                    <td className="py-4 px-3">
                      <span className="font-mono font-medium text-sm">
                        {item.sku}
                      </span>
                    </td>
                    <td className="py-4 px-3">
                      <div>
                        <p className="font-medium text-sm">
                          {item.product_name}
                        </p>
                        <p className="text-xs text-gray-500">
                          {item.product_code}
                        </p>
                      </div>
                    </td>
                    <td className="py-4 px-3 text-gray-600 text-sm">
                      {item.manufacturer || "-"}
                    </td>
                    <td className="py-4 px-3 text-gray-600 text-sm">
                      {new Date(
                        item.import_date || item.created_at
                      ).toLocaleDateString()}
                    </td>
                    <td className="py-4 px-3">
                      <span className="font-semibold text-gray-800">
                        {item.original_quantity}
                      </span>
                    </td>
                    <td className="py-4 px-3">
                      <span className="font-semibold text-green-600">
                        {item.quantity}
                      </span>
                    </td>
                    <td className="py-4 px-3">
                      <span className="font-semibold text-red-600">
                        {item.missing_quantity_number || 0}
                      </span>
                    </td>
                    <td className="py-4 px-3">JOD {item.price.toFixed(2)}</td>
                    <td className="py-4 px-3 min-w-[140px]">
                      <span
                        className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${
                          item.quantity_status === "received"
                            ? "bg-green-100 text-green-800"
                            : item.quantity_status === "under_review"
                            ? "bg-yellow-100 text-yellow-800"
                            : "bg-red-100 text-red-800"
                        }`}
                      >
                        {item.quantity_status.replace("_", " ")}
                      </span>
                    </td>
                    <td className="py-4 px-3">
                      <div className="space-y-2 min-w-[200px]">
                        <select
                          value={
                            editingStatusId === item.id
                              ? tempStatus
                              : item.quantity_status
                          }
                          onChange={(e) => {
                            setEditingStatusId(item.id);
                            setTempStatus(e.target.value);
                            if (e.target.value !== "missing_quantity") {
                              setTempMissingQty(0);
                            } else {
                              const initialMissingQty =
                                item.missing_quantity_number || 1;
                              setTempMissingQty(
                                initialMissingQty >= item.original_quantity
                                  ? 1
                                  : initialMissingQty
                              );
                            }
                          }}
                          className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-100"
                          disabled={
                            item.quantity_status !== "under_review" ||
                            (isSavingStatus && editingStatusId === item.id)
                          }
                        >
                          <option value="under_review">Under Review</option>
                          <option value="received">Received</option>
                          <option value="missing_quantity">
                            Missing Quantity
                          </option>
                        </select>
                        {editingStatusId === item.id &&
                          tempStatus === "missing_quantity" && (
                            <div>
                              <input
                                type="number"
                                min="1"
                                max={item.original_quantity - 1}
                                value={tempMissingQty}
                                onChange={(e) => {
                                  const value = parseInt(e.target.value) || 0;
                                  if (value >= item.original_quantity) {
                                    toast.error(
                                      `Missing quantity must be less than ${item.original_quantity}`
                                    );
                                    setTempMissingQty(
                                      item.original_quantity - 1
                                    );
                                  } else if (value < 1) {
                                    toast.error(
                                      "Missing quantity must be at least 1"
                                    );
                                    setTempMissingQty(1);
                                  } else {
                                    setTempMissingQty(value);
                                  }
                                }}
                                className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="Missing qty"
                                disabled={isSavingStatus}
                              />
                              <p className="text-xs text-gray-500 mt-1">
                                Available:{" "}
                                {item.original_quantity - tempMissingQty}
                              </p>
                            </div>
                          )}
                        {editingStatusId === item.id && (
                          <div className="flex gap-1">
                            <button
                              onClick={() => {
                                if (
                                  tempStatus === "missing_quantity" &&
                                  (tempMissingQty < 1 ||
                                    tempMissingQty >= item.original_quantity)
                                ) {
                                  toast.error(
                                    "Please enter a valid missing quantity (1 to " +
                                      (item.original_quantity - 1) +
                                      ")"
                                  );
                                  return;
                                }
                                handleStatusSave(item.id);
                              }}
                              disabled={isSavingStatus}
                              className="px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                            >
                              {isSavingStatus ? "Saving..." : "Save"}
                            </button>
                            <button
                              onClick={handleStatusCancel}
                              disabled={isSavingStatus}
                              className="px-2 py-1 text-xs bg-gray-300 text-gray-700 rounded hover:bg-gray-400 disabled:opacity-50"
                            >
                              Cancel
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={11}
                    className="px-6 py-8 text-center text-gray-500"
                  >
                    {t("inventory.noImportsFound") || "No import records found"}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="bg-white px-4 py-3 flex flex-col sm:flex-row items-center justify-between border-t border-gray-200 sm:px-6 space-y-4 sm:space-y-0">
          <div className="text-sm text-gray-500">
            {!isLoading && total > 0 ? (
              <>
                {t("inventory.showing")} {(currentPage - 1) * 50 + 1}-
                {Math.min(currentPage * 50, total)} {t("inventory.of")} {total}{" "}
                {t("inventory.entries")}
              </>
            ) : (
              <span>{t("inventory.noImportsFound") || "No records"}</span>
            )}
          </div>
          <div className="flex space-x-1">
            <button
              className="px-3 py-1 border rounded text-sm disabled:opacity-50 hover:bg-gray-50 transition-colors"
              disabled={currentPage === 1 || isLoading}
              onClick={() => handlePageChange(currentPage - 1)}
            >
              {t("inventory.previous")}
            </button>
            <button className="px-3 py-1 border rounded text-sm bg-blue-600 text-white">
              {currentPage} / {totalPages || 1}
            </button>
            <button
              className="px-3 py-1 border rounded text-sm disabled:opacity-50 hover:bg-gray-50 transition-colors"
              disabled={
                currentPage === totalPages || totalPages <= 1 || isLoading
              }
              onClick={() => handlePageChange(currentPage + 1)}
            >
              {t("inventory.next")}
            </button>
          </div>
        </div>
      </div>

      {/* Bulk Edit Modal */}
      {showBulkEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-2xl font-bold text-gray-800">
                    Bulk Edit Pending Items
                  </h2>
                  <p className="text-sm text-gray-600 mt-1">
                    SKU: {selectedSku} • {pendingImports.length} item(s) under
                    review
                  </p>
                </div>
                <button
                  onClick={() => setShowBulkEditModal(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                  disabled={isSavingBulk}
                >
                  <AlertCircle size={24} />
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="flex-1 overflow-y-auto p-6">
              <div className="space-y-4">
                {pendingImports.map((item) => {
                  const bulkItem = bulkEditItems.find(
                    (bi) => bi.id === item.id
                  );
                  if (!bulkItem) return null;

                  return (
                    <div
                      key={item.id}
                      className="border border-gray-200 rounded-lg p-4 bg-gray-50"
                    >
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <p className="font-semibold text-gray-800">
                            Import ID: #{item.id}
                          </p>
                          <p className="text-sm text-gray-600">
                            Quantity: {item.quantity} • Import Date:{" "}
                            {new Date(item.import_date).toLocaleDateString()}
                          </p>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Status Selection */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Status
                          </label>
                          <select
                            value={bulkItem.quantity_status}
                            onChange={(e) =>
                              handleBulkStatusChange(
                                item.id,
                                e.target.value as
                                  | "received"
                                  | "missing_quantity"
                              )
                            }
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="received">Received</option>
                            <option value="missing_quantity">
                              Missing Quantity
                            </option>
                          </select>
                        </div>

                        {/* Missing Quantity Input */}
                        {bulkItem.quantity_status === "missing_quantity" && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Missing Quantity
                            </label>
                            <input
                              type="number"
                              min="0"
                              max={item.quantity}
                              value={bulkItem.missing_quantity_number || 0}
                              onChange={(e) =>
                                handleBulkMissingQuantityChange(
                                  item.id,
                                  parseInt(e.target.value) || 0
                                )
                              }
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                            <p className="text-xs text-gray-500 mt-1">
                              Available:{" "}
                              {item.quantity -
                                (bulkItem.missing_quantity_number || 0)}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => setShowBulkEditModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
                disabled={isSavingBulk}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveBulkEdit}
                disabled={isSavingBulk}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSavingBulk ? "Saving..." : "Save All Changes"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InventoryDashboard;
